import { ActionIcon, Badge, Group, Loader, Menu, Stack, Table, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { closeAllModals, openConfirmModal } from '@mantine/modals';
import { IconCopy, IconDots } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DatasetsInfoWithProgress, useBot, useBotDatasetMethod, useCollaborateStore } from '@mygpt/app';
import { useDatasetStore } from '@/store/dataset';

import { SvgDataset, SvgLinkOff } from './icon';

interface Props {
  item: DatasetsInfoWithProgress;
  handleUnbind: () => void;
}

export const EmbeddingsItem = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAssist } = useCollaborateStore();
  const { botId } = useBot();

  const { handleUnbind, item } = props;
  const { setCloneTarget } = useDatasetStore();
  const { botDatasetBindOrUnbind, loading } = useBotDatasetMethod();

  const unbindDataset = (dataset: DatasetsInfo) => {
    openConfirmModal({
      title: t('embeddings.dataset.remove_modal.title'),
      centered: true,
      children: <span>{t('embeddings.dataset.remove_modal.desc')}</span>,
      labels: { confirm: t('alert_ok'), cancel: t('alert_cancel') },
      onCancel: () => console.log('Cancel'),
      confirmProps: {
        loading: loading,
        onClick: async () => {
          await botDatasetBindOrUnbind({
            aiId: botId || '',
            datasetIds: [dataset.id],
            type: 'unbind',
          });
          closeAllModals();
          handleUnbind();
        },
      },
    });
  };

  const isCloning = useMemo(() => {
    return ['cloning_source', 'cloning_target'].includes(item.data_status || '');
  }, [item.data_status]);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const cloneProgress = useMemo(() => {
    if (item.clone_progress.total === 0) return 0;
    return ((item.clone_progress.done / item.clone_progress.total) * 100).toFixed(0);
  }, [item.clone_progress.done, item.clone_progress.total]);

  return (
    <Table.Tr>
      <Table.Td style={{ paddingLeft: '20px' }}>
        <Group gap="xs" wrap="nowrap">
          <div className="flex-shrink-0">
            <SvgDataset />
          </div>
          <UnstyledButton
            className="flex-1 min-w-0"
            onClick={() => {
              navigate(`/${isAssist ? 'assist' : 'bot'}/${botId}/datasets/${item.id}/data-source/file`);
            }}
          >
            <Group wrap="nowrap" justify="space-between">
              <Stack gap={0} className="flex-1 min-w-0">
                <Tooltip
                  label={item.name}
                  position="top-start"
                  arrowOffset={16}
                  multiline
                  withArrow
                  className="max-w-[500px]"
                >
                  <Text lineClamp={1} className="flex-1">
                    {item.name}
                  </Text>
                </Tooltip>
                <Tooltip
                  label={item.description}
                  position="bottom-start"
                  arrowOffset={16}
                  multiline
                  withArrow
                  className="max-w-[500px]"
                >
                  <Text size="sm" c="gray.4" lineClamp={1}>
                    {item.description}
                  </Text>
                </Tooltip>
              </Stack>
              {isCloning && (
                <Badge color="green" className="flex-none">
                  <Group gap={4} wrap="nowrap">
                    <Loader size={10} color="white" />
                    {item.data_status === 'cloning_source' ? t('dataset.copying') : t('dataset.generating')}
                    {/* 后端该功能有问题，暂时屏蔽 */}
                    {/* {item.data_status === 'cloning_target' && <span>{cloneProgress}%</span>} */}
                  </Group>
                </Badge>
              )}
            </Group>
          </UnstyledButton>
        </Group>
      </Table.Td>
      <Table.Td>
        <div className="text-[14px] text-gray-500 flex items-center">{item.total_resources || '-'}</div>
      </Table.Td>

      <Table.Td>
        <Stack gap={4}>
          <Group gap={4}>
            <Text size="sm" c="gray.6">
              {t('embeddings.table.filter_by_status.learned')}：
            </Text>
            <Text size="sm">{item.status?.learned_count || '-'}</Text>
          </Group>
          <Group gap={4}>
            <Text size="sm" c="gray.6">
              {t('embeddings.table.filter_by_status.learning')}：
            </Text>
            <Text size="sm">{item.status?.learning_count || '-'}</Text>
          </Group>
          <Group gap={4}>
            <Text size="sm" c="gray.6">
              {t('embeddings.table.filter_by_status.ready')}：
            </Text>
            <Text size="sm">{item.status?.pending_count || '-'}</Text>
          </Group>
          <Group gap={4}>
            <Text size="sm" c="gray.6">
              {t('embeddings.table.filter_by_status.failed')}：
            </Text>
            <Text size="sm">{item.status?.failed_count || '-'}</Text>
          </Group>
          <Group gap={4}>
            <Text size="sm" c="gray.6">
              {t('embeddings.table.filter_by_status.unavailable')}：
            </Text>
            <Text size="sm">{item.status?.unavailable_count || '-'}</Text>
          </Group>
        </Stack>
      </Table.Td>

      <Table.Td>
        <div className="text-[14px] text-gray-500 flex items-center">
          {dayjs(item.updated_at).format('YYYY-MM-DD HH:mm:ss')}
        </div>
      </Table.Td>

      {!isAssist && (
        <Table.Td>
          <Menu shadow="md">
            <Menu.Target>
              <ActionIcon variant="subtle">
                <IconDots />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                className="text-gary-600"
                leftSection={<IconCopy />}
                onClick={() => {
                  setCloneTarget(item);
                }}
                disabled={isCloning}
              >
                {isCloning
                  ? item.data_status === 'cloning_source'
                    ? t('dataset.copying')
                    : t('dataset.generating')
                  : t('clone')}
              </Menu.Item>
              <Menu.Item
                leftSection={<SvgLinkOff />}
                onClick={() => {
                  unbindDataset(item);
                }}
              >
                {t('embeddings.dataset.remove')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      )}
    </Table.Tr>
  );
};
